<script>
import Spinner from "@/components/ui/Spinner";
import IndicationCard from "../appointment/IndicationCard.vue";
import FormRenderer from "@/components/ui/FormRenderer.vue";
import Indications from "@/components/appointment/indications/Indications.vue";
import ModalResendPrescription from "@/components/appointment/ModalResendPrescription.vue";
import { INDICATIONS_TYPES } from "@/services/constants";
import indicationService from "@/services/indication";
import formService from "@/services/form";
import { startCase, toLower, capitalize, cloneDeep } from "lodash";
import { mapGetters } from "vuex";
import { landingUrl, shopUrl } from "@/services/config";
import programPatientService from "@/services/programPatient";
import parametrizationService from "@/services/parametrization";
import fileUploadConfigService from "@/services/fileupload-config";
import api from "@/services/api";

export default {
  name: "PatientHistory",
  props: { patientId: { type: String, required: true } },
  components: {
    Spinner,
    IndicationCard,
    Indications,
    FormRenderer,
    ModalResendPrescription,
  },
  data() {
    return {
      isLoading: false,
      appointments: [],
      programs: [],
      editingIndicationId: null,
      editingIndicationData: null,
      editingIndicationType: null,
      showIndicationTitles: [
        "medications",
        "forms",
        "imagesResults",
        "labResults",
      ],
      selectedForm: null,
      allowedEditTypes: [],
      editTimeLimitInMinutes: null,
      editingFormData: null,
      selectedFormForRenderer: null,
      isFormRendererVisible: false,
      forms: [],
      resendPrescriptionVisible: false,
      resendPrescriptionIndication: null,
    };
  },
  computed: {
    ...mapGetters(["isPatient", "isAdmissionist", "isDoctor", "isAdmin"]),
    INDICATIONS_TYPES() {
      return INDICATIONS_TYPES;
    },
    iconMap() {
      const map = {};
      for (const item of INDICATIONS_TYPES) {
        if (item.type && item.micon) map[item.type] = item.micon;
      }
      return map;
    },
    groupedByType() {
      const typesMap = {};
      for (const { type } of INDICATIONS_TYPES) {
        typesMap[type] = [];
      }

      for (const appt of this.appointments) {
        for (const ind of appt.indications || []) {
          if (!typesMap[ind.type]) continue;
          let existingAppt = typesMap[ind.type].find((a) => a._id === appt._id);
          if (!existingAppt) {
            existingAppt = {
              _id: appt._id,
              startsAt: appt.startsAt,
              indications: [],
              doctor: appt.doctor,
            };
            typesMap[ind.type].push(existingAppt);
          }
          existingAppt.indications.push(ind);
        }
      }

      return typesMap;
    },
    canResendPrescription() {
      return !this.isPatient && !this.isAdmissionist;
    },
    resendIndicationTypeName() {
      if (!this.resendPrescriptionIndication) return "Indicación";
      const meta = INDICATIONS_TYPES.find(
        (t) => t.type === this.resendPrescriptionIndication.type
      );
      return meta?.name || "Indicación";
    },
    resendIndicationTitle() {
      if (!this.resendPrescriptionIndication) return "";
      return this.getIndicationTitle(this.resendPrescriptionIndication);
    },

    favoriteForms() {
      const favoriteAppointments = [];

      for (const appt of this.appointments) {
        const favoriteIndications = (appt.indications || []).filter(
          (ind) => ind.type === "forms" && ind.baseForm?.isFavorite === true
        );

        if (favoriteIndications.length > 0) {
          const favoriteAppt = {
            _id: appt._id,
            startsAt: appt.startsAt,
            indications: favoriteIndications,
            doctor: appt.doctor,
          };
          favoriteAppointments.push(favoriteAppt);
        }
      }

      return favoriteAppointments.sort(
        (a, b) => new Date(b.startsAt) - new Date(a.startsAt)
      );
    },
  },
  async mounted() {
    await this.loadData();
    await this.loadAllowedEditTypes();
    await this.loadEditTimeLimit();
    await this.loadForms();
    this.$bus.$on("open-form-modal", this.handleOpenFormModal);
  },
  beforeDestroy() {
    this.$bus.$off("open-form-modal", this.handleOpenFormModal);
  },
  methods: {
    capitalize,
    async loadAllowedEditTypes() {
      try {
        const parametrization =
          await parametrizationService.findByParameterName(
            "EDIT_TYPES_INDICATIONS_AVAILABLE"
          );
        if (
          parametrization?.parameterValue &&
          parametrization.parameterType === "array" &&
          parametrization.parameterValue.length > 0
        ) {
          this.allowedEditTypes = parametrization.parameterValue;
        }
      } catch (error) {
        console.warn(
          "No se pudo obtener EDIT_TYPES_INDICATIONS_AVAILABLE desde parametrizations:",
          error
        );
      }
    },
    async loadEditTimeLimit() {
      try {
        const parametrization =
          await parametrizationService.findByParameterName(
            "EDIT_TIME_LIMIT_IN_M"
          );
        if (
          parametrization?.parameterValue &&
          parametrization.parameterType === "number" &&
          parametrization.parameterValue > 0
        ) {
          this.editTimeLimitInMinutes = parametrization.parameterValue;
        }
      } catch (error) {
        console.warn(
          "No se pudo obtener EDIT_TIME_LIMIT_IN_M desde parametrizations:",
          error
        );
      }
    },
    formatTime(dateStr) {
      if (!dateStr) return "";
      const d = new Date(dateStr);
      return isNaN(d)
        ? ""
        : d.toLocaleTimeString("es-AR", {
            hour: "2-digit",
            minute: "2-digit",
            hour12: false,
          });
    },
    getDoctorInfo(doctor) {
      if (!doctor) return null;
      const name = [doctor.firstName, doctor.lastName]
        .filter(Boolean)
        .map(capitalize)
        .join(" ");
      if (!name) return null;
      const license =
        doctor.doctor?.license?.number ||
        doctor.license?.number ||
        doctor.matricula ||
        null;
      const licenseType =
        doctor.doctor?.license?.type || doctor.license?.type || null;
      const licenseLabel = licenseType === "mp" ? "M.P." : "M.N.";
      return license
        ? `Dr. ${name} - ${licenseLabel} ${license}`
        : `Dr. ${name}`;
    },
    getIndicationTitle(indication) {
      switch (indication.type) {
        case "medications":
          return (
            indication.medication?.name ||
            indication.publicMedication?.name ||
            "Sin nombre"
          );
        case "images":
          return "Imagen médica";
        case "lab": {
          return startCase(toLower(indication.description)) || "Laboratorio";
        }
        case "practices":
          return indication.description || "Práctica médica";
        case "imagesResults":
          return indication.description || "Resultados de imágenes";
        case "labResults":
          return indication.description || "Resultados de laboratorio";
        case "evolution":
          return indication.description || "";
        case "notes":
          return indication.description || "Nota médica";
        case "certificates":
          return indication.title || "Certificado";
        case "forms":
          return indication.name || "Formulario sin nombre";
        case "program":
          return indication.name || "Programa sin nombre";
        default:
          return "Indicación";
      }
    },
    shouldShowIndicationTitle(type) {
      return this.showIndicationTitles.includes(type);
    },
    shouldShowIcons(type) {
      return type === "imagesResults" || type === "labResults";
    },
    getIconConfigs(type) {
      const configs = {
        imagesResults: [
          {
            name: "description",
            tooltip: "Ver reportes",
            action: "viewReports",
          },
          {
            name: "radiology",
            tooltip: "Ver imágenes",
            action: "viewImages",
          },
        ],
        labResults: [
          {
            name: "description",
            tooltip: "Ver resultados",
            action: "viewResults",
          },
        ],
      };
      return configs[type] || [];
    },
    // FG: Acá deberíamos implementar las acciones específicas para cada botón
    handleIconClick(action) {
      switch (action) {
        case "viewReports":
          break;
        case "viewImages":
          break;
        case "viewResults":
          break;
        default:
          console.log("Acción no implementada:", action);
      }
    },

    async onEditIndication(indication) {
      if (indication.type === "forms" && indication.isForm) {
        let originalForm = null;

        if (indication.form?._id) {
          originalForm = this.forms.find((f) => f._id === indication.form._id);
        }

        if (!originalForm && indication.formId) {
          originalForm = this.forms.find((f) => f._id === indication.formId);
        }

        if (!originalForm && indication.name) {
          originalForm = this.forms.find((f) => f.name === indication.name);
        }

        if (originalForm) {
          this.editingIndicationId = null;
          this.editingIndicationData = null;
          this.editingIndicationType = null;

          this.selectedFormForRenderer = cloneDeep(originalForm);

          const editingData = cloneDeep(indication);
          editingData.sections = editingData.sections || [];
          if (!editingData.fileSection) {
            editingData.fileSection = {
              enable: false,
              files: [],
            };
          }

          this.editingFormData = editingData;
          this.isFormRendererVisible = true;
        } else {
          this.$message.error("No se pudo encontrar el formulario template");
          console.error("No se encontró formulario template para:", indication);
        }
      } else {
        this.isFormRendererVisible = false;
        this.selectedFormForRenderer = null;
        this.editingFormData = null;

        this.editingIndicationId = indication._id;
        this.editingIndicationData = { ...indication };
        this.editingIndicationType = indication.type;
      }
    },

    onCancelEdit() {
      this.editingIndicationId = null;
      this.editingIndicationData = null;
      this.editingIndicationType = null;
      this.isFormRendererVisible = false;
      this.selectedFormForRenderer = null;
      this.editingFormData = null;
    },

    async onSaveIndication() {
      this.editingIndicationId = null;
      this.editingIndicationData = null;
      this.editingIndicationType = null;

      await this.loadData();
    },
    getIndicationTypesInAppointment(appointment) {
      const types = new Set();
      for (const ind of appointment.indications || []) {
        types.add(ind.type);
      }
      return types;
    },
    editingIndicationForm() {
      if (!this.editingIndicationType) return null;
      const indicationMeta = INDICATIONS_TYPES.find(
        (t) => t.type === this.editingIndicationType
      );
      return (
        indicationMeta || {
          type: this.editingIndicationType,
          name: "Indicación",
        }
      );
    },
    getAdditionalIndicationTitles(appt) {
      if (!appt.indications?.length) return "";
      const additionalIndications = appt.indications.slice(1);
      return additionalIndications
        .map((ind) => this.getIndicationTitle(ind))
        .join(", ");
    },
    getIndicationTypeTooltip(appointment, typeInd) {
      const count =
        appointment.indications?.filter((ind) => ind.type === typeInd.type)
          .length || 0;
      return count > 0 ? `${typeInd.name} (${count})` : typeInd.name;
    },
    isTypeAllowed(indication) {
      if (this.isAdmissionist || this.isPatient) {
        return false;
      }
      return this.allowedEditTypes.includes(indication.type);
    },
    canEditIndication(indication) {
      if (!this.isTypeAllowed(indication)) {
        return false;
      }

      if (
        indication.type === "forms" &&
        indication.baseForm?.isFavorite === true
      ) {
        return true;
      }

      if (this.editTimeLimitInMinutes === null) {
        return true;
      }

      if (!indication.createdAt) {
        return false;
      }

      const createdAt = new Date(indication.createdAt);
      const now = new Date();
      const diffInMinutes = (now - createdAt) / (1000 * 60);

      return diffInMinutes <= this.editTimeLimitInMinutes;
    },
    async loadData() {
      this.isLoading = true;
      try {
        const [indications, forms, programPatients] = await Promise.all([
          indicationService.get(this.patientId),
          formService.getPatientFormsByPatient(this.patientId),
          programPatientService.getProgramPatientsByPatientId(this.patientId),
        ]);

        this.programs = programPatients || [];

        const appointmentsMap = {};
        for (const ind of indications) {
          const apptId = ind.appointment || `no-appointment-${ind._id}`;
          if (!appointmentsMap[apptId]) {
            appointmentsMap[apptId] = {
              _id: apptId,
              startsAt: ind.createdAt,
              indications: [],
              doctor: ind.doctor,
            };
          }
          appointmentsMap[apptId].indications.push(ind);

          if (
            ind.appointment &&
            ind.createdAt > appointmentsMap[apptId].startsAt
          ) {
            appointmentsMap[apptId].startsAt = ind.createdAt;
          }
        }

        for (const form of forms || []) {
          const apptId = form.appointment || `no-appointment-${form._id}`;
          if (!appointmentsMap[apptId]) {
            appointmentsMap[apptId] = {
              _id: apptId,
              startsAt: form.answeredAt || form.createdAt,
              indications: [],
              doctor: form.doctor,
            };
          }

          const formIndication = {
            _id: form._id,
            type: "forms",
            name: form.name || form.form?.name || "Formulario sin nombre",
            sections: form.sections,
            fileSection: form.fileSection,
            createdAt: form.answeredAt || form.createdAt,
            isForm: true,
            form: form.form || { _id: form.form?._id || form.form },
            formId: form.form?._id || form.form,
            baseForm: form.baseForm,
          };

          appointmentsMap[apptId].indications.push(formIndication);

          if (
            form.appointment &&
            (form.answeredAt || form.createdAt) >
              appointmentsMap[apptId].startsAt
          ) {
            appointmentsMap[apptId].startsAt =
              form.answeredAt || form.createdAt;
          }
        }

        for (const programPatient of this.programs || []) {
          const apptId =
            programPatient.appointment || `program-${programPatient._id}`;
          if (!appointmentsMap[apptId]) {
            appointmentsMap[apptId] = {
              _id: apptId,
              startsAt: programPatient.createdAt,
              indications: [],
              doctor: programPatient.doctor || null,
              isStandaloneProgramAppointment: !programPatient.appointment,
            };
          }

          const programIndication = {
            _id: programPatient._id,
            type: "program",
            name: programPatient.program.name,
            description: programPatient.program.description,
            status: programPatient.status,
            initialData: programPatient.initialData,
            createdAt: programPatient.createdAt,
            isProgram: true,
          };

          appointmentsMap[apptId].indications.push(programIndication);

          if (
            programPatient.appointment &&
            programPatient.createdAt > appointmentsMap[apptId].startsAt
          ) {
            appointmentsMap[apptId].startsAt = programPatient.createdAt;
          }
        }

        this.appointments = Object.values(appointmentsMap).sort(
          (a, b) => new Date(b.startsAt) - new Date(a.startsAt)
        );
      } catch (error) {
        console.error("Error al cargar datos del paciente:", error);
      } finally {
        this.isLoading = false;
      }
    },
    getActionButtons(type, appointment) {
      const buttonConfigs = {
        medications: [
          { key: "buy", text: "Comprar", class: "button-blue-new" },
          { key: "download", text: "PDF", class: "button-green-new" },
        ],
        images: [
          { key: "appointment", text: "Sacar turno", class: "button-blue-new" },
          { key: "download", text: "PDF", class: "button-green-new" },
        ],
        lab: [
          { key: "where", text: "Donde", class: "button-green-new" },
          { key: "download", text: "PDF", class: "button-green-new" },
        ],
      };

      const buttons = buttonConfigs[type] || [];

      if (type === "medications" && appointment) {
        const buyButton = buttons.find((button) => button.key === "buy");
        if (buyButton) {
          buyButton.disabled = this.isIndicationExpired(appointment.startsAt);
        }
      }

      return buttons;
    },

    isIndicationExpired(createdAt) {
      if (!createdAt) return false;

      const oneMonthAgo = new Date();
      oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);

      const indicationDate = new Date(createdAt);

      return indicationDate < oneMonthAgo;
    },
    handleActionButton(buttonKey, button) {
      if (button && button.disabled) {
        return;
      }

      switch (buttonKey) {
        case "appointment":
          window.open(landingUrl, "_blank");
          break;
        case "buy":
          window.open(shopUrl, "_blank");
          break;
        // FG: Acá deberíamos implementar las otras acciones cuando sepamos
        default:
          console.error("Acción no implementada / errónea:", buttonKey);
      }
    },

    async loadForms() {
      try {
        this.forms = await formService.getForms();
      } catch (error) {
        console.error("Error al cargar formularios:", error);
        this.forms = [];
      }
    },

    async onFormRendererUpdated(updatedForm) {
      try {
        const formId = updatedForm._id;

        const immutableFields = [
          "_id",
          "organization",
          "patient",
          "episode",
          "entity",
          "createdBy",
          "createdAt",
          "appointment",
        ];

        const payload = {};
        Object.keys(updatedForm).forEach((key) => {
          if (!immutableFields.includes(key)) {
            payload[key] = updatedForm[key];
          }
        });

        if (updatedForm.hasFiles && updatedForm.dropzoneFiles?.length > 0) {
          await this.uploadFormFiles(formId, updatedForm.dropzoneFiles);
        }

        await formService.updatePatientForm(formId, payload);

        this.$message.success("Formulario actualizado correctamente");

        this.isFormRendererVisible = false;
        this.selectedFormForRenderer = null;
        this.editingFormData = null;

        await this.loadData();
      } catch (error) {
        console.error("Error al actualizar formulario:", error);
        this.$message.error(error.response?.data?.error || error.message);
      }
    },

    async uploadFormFiles(formId, files) {
      try {
        const formData = new FormData();

        files.forEach((file) => {
          const nativeFile = file.file || file;
          if (nativeFile instanceof File || nativeFile instanceof Blob) {
            formData.append("files", nativeFile, nativeFile.name);
          }
        });

        const uploadUrl = fileUploadConfigService.getFormFilesEndpoint(formId);
        await api.put(uploadUrl, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });

        this.$message.success("Archivos adjuntos subidos exitosamente");
      } catch (error) {
        console.error("Error al subir archivos del formulario:", error);
        this.$message.error(
          `Error al subir archivos: ${
            error.response?.data?.error || error.message
          }`
        );
      }
    },

    onFormRendererCancel() {
      this.isFormRendererVisible = false;
      this.selectedFormForRenderer = null;
      this.editingFormData = null;
    },

    currentPatient() {
      return {
        _id: this.patientId,
      };
    },

    onResendPrescription(indication) {
      this.resendPrescriptionIndication = indication;
      this.resendPrescriptionVisible = true;
    },

    async onPrintPrescription(indication) {
      if (indication.pdfUrl) {
        window.open(indication.pdfUrl, "_blank");
        return;
      }

      const generatePdfTypes = [
        "lab",
        "images",
        "practices",
        "certificates",
        "notes",
      ];
      if (generatePdfTypes.includes(indication.type)) {
        try {
          const result = await indicationService.generatePdf(indication._id);
          if (result.pdfUrl) {
            this.$set(indication, "pdfUrl", result.pdfUrl);
            window.open(result.pdfUrl, "_blank");
          }
        } catch {
          this.$message.error("No se pudo generar el PDF de la indicación");
        }
        return;
      }

      this.$message.warning("No hay PDF disponible para esta indicación");
    },
  },
};
</script>

<template lang="pug">
.container
  h1 Historial de atenciones y evoluciones
  spinner(v-if="isLoading")

  // Atenciones previas
  el-collapse(v-else-if="appointments.length")
    el-collapse-item.indication-box__history.important(:name="'atenciones-previas'")
      template(#title)
        micon(name="stethoscope" color="#ffff")
        span.title Atenciones previas

      el-collapse
        el-collapse-item.indication-box__history(
          v-for="appointment in appointments"
          :key="appointment._id"
          :name="appointment._id"
          v-if="appointment.indications.length && !appointment.isMock && !appointment.isStandaloneProgramAppointment"
        )
          template(#title)
            .header-date-row
              span.left-date {{ appointment.startsAt | formatDate }}
              .indication-icons
                micon(
                  v-for="typeInd in INDICATIONS_TYPES"
                  v-if="(!isPatient || typeInd.type !== 'evolution') && typeInd.type !== 'imagesResults' && typeInd.type !== 'labResults'"
                  :key="typeInd.type"
                  :name="iconMap[typeInd.type]"
                  :color="getIndicationTypesInAppointment(appointment).has(typeInd.type) ? '#405186' : '#dbdbdb'"
                  v-tooltip="getIndicationTypeTooltip(appointment, typeInd)"
                )
              span.doctor-info-collapsed(v-if="getDoctorInfo(appointment.doctor)") {{ getDoctorInfo(appointment.doctor) }}
              span.right-time {{ formatTime(appointment.startsAt) }}
          template(v-for="ind in appointment.indications")
            FormRenderer(
              v-if="isFormRendererVisible && editingFormData && editingFormData._id === ind._id"
              :selectedForm="selectedFormForRenderer"
              :patient="currentPatient()"
              :isVisible="isFormRendererVisible"
              :prefillData="editingFormData"
              :isEditing="true"
              @form-updated="onFormRendererUpdated"
              @cancel-form="onFormRendererCancel"
            )
            indications(
              v-if="editingIndicationId === ind._id"
              :key="`indications-edit-${ind._id}`"
              :form="editingIndicationForm"
              :savedIndications="[]"
              :appointment="null"
              :existingIndication="editingIndicationData"
              :mode="'edit'"
              :typeName="INDICATIONS_TYPES.find(t => t.type === editingIndicationType)?.name || 'indicación'"
              @saved="onSaveIndication"
              @cancel="onCancelEdit"
            )
            indication-card(
              v-else
              :key="`indication-card-${ind._id}`"
              :indication="ind"
              :doctor="ind.doctor || appointment.doctor"
              :can-edit="canEditIndication(ind)"
              :is-type-allowed="isTypeAllowed(ind)"
              :edit-time-limit-in-minutes="editTimeLimitInMinutes"
              :can-resend="canResendPrescription"
              @edit-evolution="onEditIndication"
              @print-prescription="onPrintPrescription"
              @resend-prescription="onResendPrescription"
            )

  // Formularios favoritos
  el-collapse(v-if="favoriteForms.length")
    el-collapse-item.indication-box__history(:name="'formularios-favoritos'")
      template(#title)
        micon(name="favorite")
        span.title Formularios favoritos

      el-collapse
        .appointment-item(v-for="appt in favoriteForms" :key="appt._id")
          .appointment-header
            el-collapse-item.indication-box__history(:name="appt._id")
              template(#title)
                .header-date-row
                  span.left-date {{ appt.startsAt | formatDate }}
                  div.indication-container
                    span.indication-title-ellipsis {{ getIndicationTitle(appt.indications[0]) }}
                    span.indication-count(
                      v-if="appt.indications.length > 1"
                      v-tooltip="getAdditionalIndicationTitles(appt)"
                    ) +{{ appt.indications.length - 1 }}
                  span.right-time {{ formatTime(appt.startsAt) }}
              template(v-for="ind in appt.indications")
                FormRenderer(
                  v-if="isFormRendererVisible && editingFormData && editingFormData._id === ind._id"
                  :selectedForm="selectedFormForRenderer"
                  :patient="currentPatient()"
                  :isVisible="isFormRendererVisible"
                  :prefillData="editingFormData"
                  :isEditing="true"
                  @form-updated="onFormRendererUpdated"
                  @cancel-form="onFormRendererCancel"
                )
                indication-card(
                  v-else
                  :key="`indication-card-${ind._id}`"
                  :indication="ind"
                  :doctor="ind.doctor || appt.doctor"
                  :can-edit="canEditIndication(ind)"
                  :is-type-allowed="isTypeAllowed(ind)"
                  :edit-time-limit-in-minutes="editTimeLimitInMinutes"
                  :can-resend="canResendPrescription"
                  @edit-evolution="onEditIndication"
                  @print-prescription="onPrintPrescription"
                  @resend-prescription="onResendPrescription"
                )

  // Collapse por tipo de indicación
  el-collapse(v-if="appointments.length")
    el-collapse-item.indication-box__history(
      v-for="typeInd in INDICATIONS_TYPES"
      :key="typeInd.type"
      :name="typeInd.type"
      v-if="groupedByType[typeInd.type] && (!isPatient || typeInd.type !== 'evolution')"
    )
      template(#title)
        micon(:name="iconMap[typeInd.type]")
        span.title {{ typeInd.name }}

      p(v-if="!groupedByType[typeInd.type].length") No hay {{ typeInd.name }} guardadas/os para este paciente.
      el-collapse(v-else="groupedByType[typeInd.type].length")
        .appointment-item(v-for="appt in groupedByType[typeInd.type]" :key="appt._id")
          .appointment-header
            el-collapse-item.indication-box__history(:name="appt._id")
              template(#title)
                .header-date-row
                  span.left-date {{ appt.startsAt | formatDate }}
                  div.indication-container(v-if="shouldShowIndicationTitle(typeInd.type)")
                    // Mostrar íconos
                    div.indication-icons-container(v-if="shouldShowIcons(typeInd.type)")
                      button.icon-button(
                        v-for="iconConfig in getIconConfigs(typeInd.type)"
                        :key="iconConfig.name"
                        v-tooltip="iconConfig.tooltip"
                        @click.stop="handleIconClick(iconConfig.action, typeInd.type)"
                      )
                        micon(:name="iconConfig.name" color="#405186")
                    // Mostrar título normal
                    template(v-else)
                      span.indication-title-ellipsis {{ getIndicationTitle(appt.indications[0]) }}
                      span.indication-count(
                        v-if="appt.indications.length > 1"
                        v-tooltip="getAdditionalIndicationTitles(appt)"
                      ) +{{ appt.indications.length - 1 }}
                  span.doctor-info-collapsed(v-if="getDoctorInfo(appt.doctor)") {{ getDoctorInfo(appt.doctor) }}
                  span.right-time {{ formatTime(appt.startsAt) }}
              template(v-for="ind in appt.indications")
                FormRenderer(
                  v-if="isFormRendererVisible && editingFormData && editingFormData._id === ind._id"
                  :selectedForm="selectedFormForRenderer"
                  :patient="currentPatient()"
                  :isVisible="isFormRendererVisible"
                  :prefillData="editingFormData"
                  :isEditing="true"
                  @form-updated="onFormRendererUpdated"
                  @cancel-form="onFormRendererCancel"
                )
                indications(
                  v-if="editingIndicationId === ind._id"
                  :key="`indications-edit-${ind._id}`"
                  :form="editingIndicationForm"
                  :savedIndications="[]"
                  :appointment="null"
                  :existingIndication="editingIndicationData"
                  :mode="'edit'"
                  :typeName="INDICATIONS_TYPES.find(t => t.type === editingIndicationType)?.name || 'indicación'"
                  @saved="onSaveIndication"
                  @cancel="onCancelEdit"
                )
                indication-card(
                  v-else
                  :key="`indication-card-${ind._id}`"
                  :indication="ind"
                  :doctor="ind.doctor || appt.doctor"
                  :hide-doctor-info="true"
                  :can-edit="canEditIndication(ind)"
                  :is-type-allowed="isTypeAllowed(ind)"
                  :edit-time-limit-in-minutes="editTimeLimitInMinutes"
                  :can-resend="canResendPrescription"
                  @edit-evolution="onEditIndication"
                  @print-prescription="onPrintPrescription"
                  @resend-prescription="onResendPrescription"
                )
          .action-buttons(v-if="isPatient && getActionButtons(typeInd.type, appt).length")
            button.button-blue-new.action-button(
              v-for="button in getActionButtons(typeInd.type, appt)"
              :key="button.key"
              :class="button.class"
              :disabled="button.disabled"
              v-tooltip="button.disabled ? 'Han pasado más de 30 días desde su generación' : ''"
              @click="handleActionButton(button.key, button)"
            ) {{ button.text }}

  modal-resend-prescription(
    :visible.sync="resendPrescriptionVisible"
    :indication="resendPrescriptionIndication"
    :patient-id="patientId"
    :indication-type-name="resendIndicationTypeName"
    :indication-title="resendIndicationTitle"
  )
</template>

<style>
.container {
  h1 {
    margin-bottom: 35px;
  }
}

.logo {
  margin-right: 5px;
}

.header-date-row {
  flex: 1;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 14px;
  min-height: 50px;

  .indication-count {
    border: 2px solid #405186;
    color: #405186;
    border-radius: 100%;
    padding: 5px;
    font-size: 12px;
    margin-left: 5px;
    vertical-align: middle;
  }

  .indication-icons {
    display: flex;
    max-width: fit-content;
    gap: 8px;
    align-items: center;
    flex: 1;
    justify-content: center;
    margin: 0 15px;
  }
}

.left-date {
  font-weight: 600;
  min-width: 90px;
  flex-shrink: 0;
}

span.title {
  margin-left: 10px;
}

.header-date-row
  span:not(.left-date):not(.right-time):not(.indication-title-ellipsis):not(
    .indication-count
  ):not(.doctor-info-collapsed) {
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
  text-align: center;
}

.doctor-info-collapsed {
  font-size: 11px;
  font-weight: 400;
  color: #6b7aad;
  flex-shrink: 0;
  margin-left: auto;
  margin-right: 10px;
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: right;
}

.right-time {
  font-weight: 600;
  min-width: 50px;
  flex-shrink: 0;
  text-align: right;
}

.indication-box__history {
  border: solid 2px #b3bde7;
  border-radius: 10px;
  overflow: hidden;
  margin: 15px 0;

  .el-collapse-item__header {
    font-weight: 600;
  }

  .indication-box__history {
    margin: 5px 0;
  }

  .el-collapse-item__wrap {
    background-color: #d9dee7;
    padding: 15px 15px;
  }
}

.el-collapse {
  border: none !important;
}

.el-collapse-item.is-active {
  background-color: #405186;
  padding-bottom: 0;
}

.appointment-item {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 5px;

  .appointment-header {
    flex: 1;
  }

  .action-buttons {
    display: flex;
    align-self: flex-start;
    flex-wrap: wrap;
    gap: 5px;
    margin-top: 5px;

    .action-button {
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 8px 12px;
      font-size: 12px;
      white-space: nowrap;
      height: 40px;
      width: 80px;
      min-width: 80px;

      &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
        background-color: #cccccc !important;
        color: #666666 !important;
        border-color: #cccccc !important;
      }
    }
  }
}

.el-collapse-item__content {
  padding-bottom: 0 !important;
}

.el-collapse-item__header {
  background-color: white;
  padding: 0px 16px;
  font-weight: 500;
  display: flex;
  align-items: center;
}

.el-collapse-item.important {
  border-radius: 16px;
  border: none;
}

.el-collapse-item.important > div > .el-collapse-item__header {
  background-color: #4b5f95;
  color: #ffffff !important;
  font-weight: 500;
  border: none;
}

.el-collapse-item__header.is-active {
  background-color: #556ba385;
  color: white;
  font-weight: 500;

  svg {
    fill: #ffff;
  }
}

.el-collapse-item__wrap .el-collapse-item__header {
  padding: 5px 16px !important;
  height: 38px !important;
  color: #303133 !important;
  font-weight: 600 !important;
  display: flex !important;
  align-items: center !important;
}

.el-collapse-item__wrap .el-collapse-item__header.is-active {
  background-color: #cdd3ea !important;
  color: #303133 !important;
  font-weight: 600 !important;
}

.el-collapse-item__wrap .el-collapse-item__wrap {
  background-color: #ffffff;
}

.indication-container {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  max-width: 100%;
  flex: 1;
}

.indication-title-ellipsis {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
  max-width: 100%;
  flex: 1;
}

.indication-icons-container {
  display: flex;
  gap: 8px;
  align-items: center;
}

.icon-button {
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  border-radius: 4px;
  transition: background-color 0.2s;

  &:hover {
    background-color: #f0f0f0;
  }

  &:active {
    background-color: #e0e0e0;
  }
}

.form-card {
  margin-bottom: 15px;
  padding: 15px;
  background-color: #f8f9fa;
  border-radius: 8px;
  border-left: 4px solid #405186;
}

.form-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
  padding-bottom: 10px;
  border-bottom: 1px solid #e0e0e0;
}

.form-name {
  font-weight: 600;
  color: #333;
  font-size: 16px;
}

.form-status {
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;

  &.closed {
    background-color: #f0f9ff;
    color: #0369a1;
    border: 1px solid #bae6fd;
  }

  &.open {
    background-color: #f0fdf4;
    color: #166534;
    border: 1px solid #bbf7d0;
  }
}

.form-details {
  margin-top: 10px;
}

.form-section {
  margin-bottom: 20px;

  &:last-child {
    margin-bottom: 0;
  }
}

.section-title {
  color: #405186;
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 10px;
  padding-bottom: 5px;
  border-bottom: 1px solid #e0e0e0;
}

.section-questions {
  display: grid;
  gap: 8px;
}

.question {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.question-label {
  font-weight: 500;
  color: #555;
  font-size: 13px;
}

.question-answer {
  color: #333;
  font-size: 14px;
  padding-left: 10px;
  border-left: 2px solid #e0e0e0;
}
</style>
